'use client'

import { useEffect, useState, useTransition, type ReactNode } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { House, RotateCw } from 'lucide-react'
import { useTranslations } from 'next-intl'

import { PageState } from '@/components/encuentro/page/empty-state'

/*
 * The pages Encuentro shows when the route itself is gone (ALU-115): the app's
 * 404 (`app/not-found.tsx`), the root error boundary (`app/error.tsx`) and the
 * org one (`app/in/[orgSlug]/error.tsx`). None of them has the frame: the
 * boundary replaced the layout that drew it. So each is a whole page: the
 * Alumnia lockup on top (a way home) and a `PageState` (the Encuadre motif,
 * the `h1`, one true sentence and at most two actions).
 *
 * They never report the error: the route file does it once, whichever tree
 * (classic or Encuentro) CSS keeps. They never read the UI version either: the
 * route renders both trees and `UiVersionFallback` keeps one, so a failed
 * lookup (no `data-ui` on `<html>`) simply shows the classic page.
 */

function AppStateShell({ homeHref, children }: { homeHref: string; children: ReactNode }) {
  const t = useTranslations('encuentro.page')
  return (
    <div
      data-slot="app-state"
      className="fixed inset-0 grid grid-cols-[minmax(0,1fr)] grid-rows-[auto_1fr] overflow-y-auto bg-en-bg font-en-sans text-en-ink pt-safe pb-safe pl-safe pr-safe md:bg-en-canvas"
    >
      <div className="flex min-h-16 items-center px-4 py-3 md:px-8">
        <Link
          href={homeHref}
          aria-label={t('appHome')}
          className="rounded-en-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
        >
          <Image
            src="/brand/encuentro/lockup-horizontal-blue.svg"
            alt=""
            width={124}
            height={32}
            unoptimized
            className="h-8 w-auto"
          />
        </Link>
      </div>
      <main
        id="main"
        tabIndex={-1}
        className="grid grid-cols-[minmax(0,1fr)] content-start justify-items-center px-4 pb-10 outline-none md:content-center md:px-8"
      >
        <div className="w-full max-w-lg md:rounded-en-lg md:bg-en-surface md:shadow-en-card">
          {children}
        </div>
      </main>
    </div>
  )
}

/**
 * An error boundary's page: "Volver a intentar" asks the server for fresh data
 * and resets the boundary (as `ErrorState`), "Ir al inicio" leaves.
 */
export function AppError({
  error,
  reset,
  homeHref,
}: {
  error: Error & { digest?: string }
  reset: () => void
  homeHref: string
}) {
  const t = useTranslations('encuentro.page')
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const retry = () =>
    startTransition(() => {
      router.refresh()
      reset()
    })

  return (
    <AppStateShell homeHref={homeHref}>
      <PageState
        title={t('appError.title')}
        body={t('appError.body')}
        note={
          error.digest ? (
            <p className="text-en-meta text-en-ink-muted [overflow-wrap:anywhere]">
              {t('error.reference', { id: error.digest })}
            </p>
          ) : null
        }
        action={{ label: t('appError.retry'), icon: RotateCw, onClick: retry, disabled: isPending }}
        secondaryAction={{ label: t('appError.home'), icon: House, href: homeHref }}
      />
    </AppStateShell>
  )
}

/**
 * The app's 404: "Ir al inicio", and "Volver" only when the previous page is
 * ours (a 404 opened cold, from an email or a search, has nowhere safe to go
 * back to). Same rule as the classic page.
 */
export function AppNotFound({ homeHref = '/' }: { homeHref?: string }) {
  const t = useTranslations('encuentro.page')
  const router = useRouter()
  const [canGoBack, setCanGoBack] = useState(false)
  useEffect(() => {
    let sameOrigin = !document.referrer
    try {
      sameOrigin = sameOrigin || new URL(document.referrer).origin === window.location.origin
    } catch {
      sameOrigin = false
    }
    // History and referrer only exist in the browser: read once after mounting.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCanGoBack(window.history.length > 1 && sameOrigin)
  }, [])

  return (
    <AppStateShell homeHref={homeHref}>
      <PageState
        title={t('notFound.title')}
        body={t('notFound.body')}
        action={{ label: t('notFound.home'), icon: House, href: homeHref }}
        secondaryAction={
          canGoBack ? { label: t('notFoundBack'), onClick: () => router.back() } : undefined
        }
      />
    </AppStateShell>
  )
}
